import { useEffect, useState } from "react"
import {useParams, useNavigate, useLocation} from "react-router-dom"
import Header from "@/components/layout/Header"
import Footer from "@/components/layout/Footer"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { useAuth } from "@/contexts/AuthContext"
import {getGuestOrderDetail, getOrderDetail} from "@/api/orderApi"

// WAIT(입금대기) 추가
const statusMap: Record<string, string> = {
    CREATED: "주문접수",
    WAIT: "입금대기",
    PAID: "결제완료",
    PREPARING: "배송준비중",
    SHIPPING: "배송중",
    DONE: "배송완료",
    CANCELED: "주문취소"
}

// POST_PAY(무통장/현장결제) 추가
const methodMap: Record<string, string> = {
    CARD: "신용/체크카드",
    BANK: "무통장 입금",
    KAKAOPAY: "카카오페이",
    POST_PAY: "무통장/현장결제"
}

// 메시지 타입 한글 변환
const getMessageTypeName = (type: string) => {
    switch (type) {
        case "ribbon": return "리본";
        case "card": return "카드";
        case "ribbon_card": return "리본 + 카드";
        default: return "선택 안함";
    }
};

export default function OrderDetailPage() {
    const { orderNo } = useParams<{ orderNo: string }>()
    const navigate = useNavigate()
    const { isLoggedIn, isLoading } = useAuth()

    const location = useLocation()
    const guestPassword = location.state?.orderPassword

    const [order, setOrder] = useState<any>(null)
    const [pageLoading, setPageLoading] = useState(true)

    useEffect(() => {
        if (isLoading) return

        if (!isLoggedIn && !guestPassword) {
            alert("로그인이 필요하거나 비정상적인 접근입니다.")
            navigate("/login")
            return
        }
        if (!orderNo) return

        const fetchDetail = async () => {
            try {
                let response;
                if (guestPassword) {
                    response = await getGuestOrderDetail({ orderNo, orderPassword: guestPassword })
                } else {
                    response = await getOrderDetail(orderNo)
                }
                setOrder(response.data)
            } catch (error) {
                console.error("주문 상세 조회 실패:", error)
                alert("존재하지 않거나 권한이 없는 주문입니다.")
                navigate("/")
            } finally {
                setPageLoading(false)
            }
        }
        fetchDetail()
    }, [isLoggedIn, isLoading, orderNo, navigate, guestPassword])

    if (pageLoading || isLoading) return <div className="p-20 text-center text-slate-500">주문 상세 정보를 불러오는 중입니다...</div>
    if (!order) return null

    return (
      <div className="min-h-screen bg-slate-50 text-slate-900">
          <Header />

          <main className="mx-auto max-w-4xl px-4 py-8 lg:px-6">
              <div className="mb-8">
                  <Button
                    variant="ghost"
                    className="mb-2 h-auto p-0 text-sm text-slate-500 hover:bg-transparent hover:text-slate-900"
                    onClick={() => navigate("/mypage")}
                  >
                      &lt; 마이페이지로 돌아가기
                  </Button>
                  <h1 className="mt-2 text-3xl font-bold">주문 상세 내역</h1>
              </div>

              <div className="space-y-6">
                  {/* 주문 요약 헤더 */}
                  <Card className="rounded-[28px] border border-slate-200 shadow-sm bg-white">
                      <CardContent className="p-6 sm:p-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                          <div>
                              <p className="text-sm text-slate-500">주문일자: {order.createdAt.substring(0, 16).replace("T", " ")}</p>
                              <p className="text-xl font-bold mt-1 text-slate-900">주문번호 {order.orderNo}</p>
                          </div>
                          {/* 💡 수정됨: WAIT 상태일 때 붉은색 배경 테두리 표시 */}
                          <span className={`rounded-full px-4 py-2 text-sm font-bold border ${
                            order.orderStatus === 'WAIT'
                              ? 'bg-rose-50 text-rose-600 border-rose-100'
                              : 'bg-indigo-50 text-indigo-700 border-indigo-100'
                          }`}>
                                {statusMap[order.orderStatus] || order.orderStatus}
                            </span>
                      </CardContent>
                  </Card>

                  {/* 주문 상품 목록 */}
                  <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                      <CardHeader>
                          <CardTitle className="text-xl">주문 상품</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                          {order.orderItems.map((item: any, idx: number) => (
                            <div key={idx} className="flex gap-4 rounded-2xl border border-slate-200 p-4 bg-slate-50 items-center">
                                <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-slate-200 cursor-pointer" onClick={() => navigate(`/product/${item.productId}`)}>
                                    {item.thumbnailUrl ? (
                                      <img src={item.thumbnailUrl} alt={item.productNameSnapshot} className="h-full w-full object-cover" />
                                    ) : (
                                      <div className="h-full w-full flex items-center justify-center text-xs text-slate-400">No Img</div>
                                    )}
                                </div>
                                <div className="flex flex-1 flex-col sm:flex-row justify-between sm:items-center gap-2">
                                    <div>
                                        <h3 className="text-base font-semibold hover:underline cursor-pointer" onClick={() => navigate(`/product/${item.productId}`)}>
                                            {item.productNameSnapshot}
                                        </h3>
                                        <p className="mt-1 text-sm text-slate-500">수량: {item.quantity}개</p>
                                    </div>
                                    <p className="text-lg font-bold">{(item.productPriceSnapshot * item.quantity).toLocaleString("ko-KR")}원</p>
                                </div>
                            </div>
                          ))}
                      </CardContent>
                  </Card>

                  {/*<div className="grid gap-6 md:grid-cols-2">*/}
                  {/*    /!* 배송지 정보 *!/*/}
                  {/*    <Card className="rounded-[28px] border border-slate-200 shadow-sm h-full">*/}
                  {/*        <CardHeader>*/}
                  {/*            <CardTitle className="text-xl">배송지 정보</CardTitle>*/}
                  {/*        </CardHeader>*/}
                  {/*        <CardContent className="space-y-4 text-sm">*/}
                  {/*            <div className="grid grid-cols-[80px_1fr] gap-2">*/}
                  {/*                <span className="text-slate-500">받는 분</span>*/}
                  {/*                <span className="font-medium">{order.receiverName}</span>*/}
                  {/*            </div>*/}
                  {/*            <div className="grid grid-cols-[80px_1fr] gap-2">*/}
                  {/*                <span className="text-slate-500">연락처</span>*/}
                  {/*                <span className="font-medium">{order.receiverPhone}</span>*/}
                  {/*            </div>*/}
                  {/*            <div className="grid grid-cols-[80px_1fr] gap-2">*/}
                  {/*                <span className="text-slate-500">주소</span>*/}
                  {/*                <span className="font-medium">*/}
                  {/*                      {order.zipcode && `[${order.zipcode}] `}*/}
                  {/*                    {order.address1} {order.address2}*/}
                  {/*                  </span>*/}
                  {/*            </div>*/}
                  {/*            <div className="grid grid-cols-[80px_1fr] gap-2">*/}
                  {/*                <span className="text-slate-500">배송메모</span>*/}
                  {/*                <span className="font-medium">{order.deliveryMemo || "-"}</span>*/}
                  {/*            </div>*/}
                  {/*        </CardContent>*/}
                  {/*    </Card>*/}

                  {/*    /!* 결제 정보 *!/*/}
                  {/*    <Card className="rounded-[28px] border border-slate-200 shadow-sm h-full flex flex-col">*/}
                  {/*        <CardHeader>*/}
                  {/*            <CardTitle className="text-xl">결제 정보</CardTitle>*/}
                  {/*        </CardHeader>*/}
                  {/*        <CardContent className="space-y-4 text-sm flex-1 flex flex-col justify-between">*/}
                  {/*            <div className="space-y-4">*/}
                  {/*                <div className="flex justify-between">*/}
                  {/*                    <span className="text-slate-500">상품 총 금액</span>*/}
                  {/*                    <span className="font-medium">{order.productAmount.toLocaleString("ko-KR")}원</span>*/}
                  {/*                </div>*/}
                  {/*                <div className="flex justify-between">*/}
                  {/*                    <span className="text-slate-500">배송비</span>*/}
                  {/*                    <span className="font-medium">{order.shippingAmount === 0 ? "무료" : `+${order.shippingAmount.toLocaleString("ko-KR")}원`}</span>*/}
                  {/*                </div>*/}
                  {/*                <div className="flex justify-between">*/}
                  {/*                    <span className="text-slate-500">할인 금액</span>*/}
                  {/*                    <span className="font-medium text-rose-500">- {order.discountAmount.toLocaleString("ko-KR")}원</span>*/}
                  {/*                </div>*/}
                  {/*                <div className="flex justify-between">*/}
                  {/*                    <span className="text-slate-500">결제 수단</span>*/}
                  {/*                    /!* 💡 수정됨: 정상적으로 "무통장/현장결제" 한글 변환 출력 *!/*/}
                  {/*                    <span className="font-medium">{methodMap[order.paymentMethod] || order.paymentMethod}</span>*/}
                  {/*                </div>*/}
                  {/*            </div>*/}

                  {/*            <div className="mt-auto">*/}
                  {/*                <Separator className="mb-4 mt-6" />*/}
                  {/*                <div className="flex justify-between items-center">*/}
                  {/*                    <span className="text-base font-bold text-slate-900">최종 결제 금액</span>*/}
                  {/*                    <span className="text-2xl font-bold text-indigo-600">{order.totalAmount.toLocaleString("ko-KR")}원</span>*/}
                  {/*                </div>*/}

                  {/*                {order.receiptUrl && (*/}
                  {/*                  <div className="mt-5 flex justify-end">*/}
                  {/*                      <Button*/}
                  {/*                        variant="outline"*/}
                  {/*                        className="w-full sm:w-auto rounded-xl border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"*/}
                  {/*                        onClick={() => window.open(order.receiptUrl, '_blank')}*/}
                  {/*                      >*/}
                  {/*                          🧾 결제 영수증 (매출전표) 출력*/}
                  {/*                      </Button>*/}
                  {/*                  </div>*/}
                  {/*                )}*/}
                  {/*            </div>*/}
                  {/*        </CardContent>*/}
                  {/*    </Card>*/}
                  {/*</div>*/}

                  <div className="grid gap-6 md:grid-cols-2">
                      {/* 1. 배송지 정보 (기존 유지) */}
                      <Card className="rounded-[28px] border border-slate-200 shadow-sm h-full">
                          <CardHeader>
                              <CardTitle className="text-xl">배송지 정보</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4 text-sm">
                              <div className="grid grid-cols-[80px_1fr] gap-2">
                                  <span className="text-slate-500">받는 분</span>
                                  <span className="font-medium">{order.receiverName}</span>
                              </div>
                              <div className="grid grid-cols-[80px_1fr] gap-2">
                                  <span className="text-slate-500">연락처</span>
                                  <span className="font-medium">{order.receiverPhone}</span>
                              </div>
                              <div className="grid grid-cols-[80px_1fr] gap-2">
                                  <span className="text-slate-500">주소</span>
                                  <span className="font-medium break-keep">
                                      {order.zipcode && `[${order.zipcode}] `}
                                      {order.address1} {order.address2}
                                  </span>
                              </div>
                              <div className="grid grid-cols-[80px_1fr] gap-2">
                                  <span className="text-slate-500">배송메모</span>
                                  <span className="font-medium">{order.deliveryMemo || "-"}</span>
                              </div>
                          </CardContent>
                      </Card>

                      {/* 2. 배송 및 행사 일정 (🔥 신규 추가) */}
                      <Card className="rounded-[28px] border border-slate-200 shadow-sm h-full">
                          <CardHeader>
                              <CardTitle className="text-xl">배송 및 행사 일정</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4 text-sm">
                              <div className="grid grid-cols-[80px_1fr] gap-2">
                                  <span className="text-slate-500">요청일</span>
                                  <span className="font-bold text-slate-900">{order.deliveryDate || "-"}</span>
                              </div>
                              <div className="grid grid-cols-[80px_1fr] gap-2">
                                  <span className="text-slate-500">타입</span>
                                  <span className="font-medium text-indigo-600">
                                      {order.deliveryType === 'specified'
                                        ? `시간지정 (${order.deliveryTime})`
                                        : '즉시 배송'}
                                  </span>
                              </div>
                              <div className="grid grid-cols-[80px_1fr] gap-2">
                                  <span className="text-slate-500">행사 시간</span>
                                  <span className="font-medium text-slate-900">{order.eventTime || "-"}</span>
                              </div>
                          </CardContent>
                      </Card>

                      {/* 3. 메시지 정보 (🔥 신규 추가) */}
                      <Card className="rounded-[28px] border border-slate-200 shadow-sm h-full">
                          <CardHeader>
                              <CardTitle className="text-xl">메시지 정보</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4 text-sm">
                              <div className="flex justify-between border-b border-slate-100 pb-3">
                                  <span className="text-slate-500">선택 옵션</span>
                                  <span className="font-bold text-emerald-600">
                                      {getMessageTypeName(order.messageType)}
                                  </span>
                              </div>

                              {['ribbon', 'ribbon_card'].includes(order.messageType) && (
                                <div className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-4 space-y-3">
                                    <div>
                                        <p className="text-xs font-bold text-emerald-700 mb-1">경조사어 (리본 왼쪽)</p>
                                        <p className="text-slate-800 font-medium">{order.ribbonPhrase}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-bold text-emerald-700 mb-1">보내는 분 (리본 오른쪽)</p>
                                        <p className="text-slate-800 font-medium">{order.ribbonSender}</p>
                                    </div>
                                </div>
                              )}

                              {['card', 'ribbon_card'].includes(order.messageType) && (
                                <div className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-4 mt-3">
                                    <p className="text-xs font-bold text-emerald-700 mb-1">카드 내용</p>
                                    <p className="text-slate-800 font-medium whitespace-pre-wrap">{order.cardContent}</p>
                                </div>
                              )}

                              {(!order.messageType || order.messageType === 'none') && (
                                <div className="py-6 text-center text-sm text-slate-400 bg-slate-50 rounded-xl">
                                    요청하신 메시지가 없습니다.
                                </div>
                              )}
                          </CardContent>
                      </Card>

                      {/* 4. 결제 정보 (기존 유지) */}
                      <Card className="rounded-[28px] border border-slate-200 shadow-sm h-full flex flex-col">
                          <CardHeader>
                              <CardTitle className="text-xl">결제 정보</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4 text-sm flex-1 flex flex-col justify-between">
                              <div className="space-y-4">
                                  <div className="flex justify-between">
                                      <span className="text-slate-500">상품 총 금액</span>
                                      <span className="font-medium">{order.productAmount?.toLocaleString("ko-KR")}원</span>
                                  </div>
                                  <div className="flex justify-between">
                                      <span className="text-slate-500">배송비</span>
                                      <span className="font-medium">{order.shippingAmount === 0 ? "무료" : `+${order.shippingAmount?.toLocaleString("ko-KR")}원`}</span>
                                  </div>
                                  <div className="flex justify-between">
                                      <span className="text-slate-500">할인 금액</span>
                                      <span className="font-medium text-rose-500">- {order.discountAmount?.toLocaleString("ko-KR")}원</span>
                                  </div>
                                  <div className="flex justify-between">
                                      <span className="text-slate-500">결제 수단</span>
                                      <span className="font-medium">{methodMap[order.paymentMethod] || order.paymentMethod}</span>
                                  </div>
                              </div>

                              <div className="mt-auto">
                                  <Separator className="mb-4 mt-6" />
                                  <div className="flex justify-between items-center">
                                      <span className="text-base font-bold text-slate-900">최종 결제 금액</span>
                                      <span className="text-2xl font-bold text-indigo-600">{order.totalAmount?.toLocaleString("ko-KR")}원</span>
                                  </div>

                                  {order.receiptUrl && (
                                    <div className="mt-5 flex justify-end">
                                        <Button
                                          variant="outline"
                                          className="w-full sm:w-auto rounded-xl border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                                          onClick={() => window.open(order.receiptUrl, '_blank')}
                                        >
                                            🧾 결제 영수증 (매출전표) 출력
                                        </Button>
                                    </div>
                                  )}
                              </div>
                          </CardContent>
                      </Card>
                  </div>
              </div>
          </main>

          <Footer />
      </div>
    )
}